import {
	type Message,
	StyledPillButton,
	clientConfig,
	colors,
	focusVisible,
	getSdk,
	strings,
	text,
} from "@quickpoint.me/shared";
import { GraphQLClient } from "graphql-request";
import { getAuth, getIdToken } from "firebase/auth";
import { type FormEvent, useState } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { FileTypeToDisplay } from "../../state/fileState";
import { HostId, OwnerName, StackId } from "../../state/uploaderState";
import { useSetRecoilState } from "recoil";
import Countdown from "../../icons/countdown.svg?react";
import { spinAnimation } from "../../lib/animations";
import ToggleFilesPanelButton from "../ToggleFilesPanel";

interface SearchResult {
	thumbnailUrl: string;
	fullImageUrl: string;
	title: string;
	source: string;
	sourcePageUrl: string;
	license: string;
}

/* overlays the whole files modal (tabs and action row included) so the
   results get the full surface — StyledFiles is the positioning context */
const StyledSearch = styled.div`
	position: absolute;
	inset: 0;
	z-index: 10;
	background: ${colors.white};
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-height: 0;
	padding: 1rem;
	font-family: ${(props) => props.theme.font};
`;

const StyledForm = styled.form`
	display: flex;
	gap: 0.6rem;
	align-items: stretch;
`;

const StyledSearchInput = styled.input`
	flex: 1;
	min-width: 0;
	padding: 0.6rem 0.8rem;
	border: 1px solid #757575; /* 3:1 on white (WCAG 1.4.11) */
	border-radius: 6px;
	font-family: inherit;
	font-size: 0.95rem;
	${focusVisible}

	&::placeholder {
		color: ${colors.textSafe.grey};
		opacity: 1;
	}
`;

const StyledIntro = styled.p`
	margin: 0;
	color: ${colors.textSafe.grey};
	font-size: 0.85rem;
`;

/* Back on the left, the modal's Done on the right — the overlay hides the
   header, so it must offer its own way out */
const StyledTopRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 0.6rem;
`;

const StyledBack = styled.button.attrs({ type: "button" })`
	align-self: flex-start;
	background: none;
	border: none;
	cursor: pointer;
	color: #5335c9;
	font-family: inherit;
	font-size: 0.85rem;
	font-weight: 500;
	padding: 0.3rem 0.4rem;
	border-radius: 6px;
	min-height: 44px;
	${focusVisible}

	&:hover {
		text-decoration: underline;
	}
`;

const StyledStatus = styled.p`
	margin: 0;
	color: ${colors.textSafe.grey};
	font-size: 0.9rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const Spinner = styled(Countdown)`
	width: 1.1rem;
	height: 1.1rem;
	flex-shrink: 0;
	animation: ${spinAnimation};
`;

/* spinner centred over the tile that is being added */
const TileSpinner = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;

	svg {
		width: 2rem;
		height: 2rem;
		animation: ${spinAnimation};
	}
`;

const StyledResults = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	overflow-y: auto;
	overflow-x: hidden;
	min-height: 0;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 0.75rem;
`;

const StyledResult = styled.li`
	position: relative;
`;

/* the whole tile is the button — click to add. Brand hairline + lavender
   caption strip; the thumbnail fills the frame. */
const StyledResultButton = styled.button.attrs({ type: "button" })<{
	$busy: boolean;
}>`
	width: 100%;
	aspect-ratio: 1 / 1;
	border: 1px solid ${(props) => props.theme.navigationFrame};
	border-radius: 8px;
	overflow: hidden;
	padding: 0;
	cursor: ${(props) => (props.$busy ? "progress" : "pointer")};
	background: ${(props) => props.theme.navigationBackGround};
	display: block;
	position: relative;
	${focusVisible}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		opacity: ${(props) => (props.$busy ? 0.4 : 1)};
		transition: opacity 150ms ease;
	}

	/* caption always visible — it is the name the image is filed under, so
	   the user can find it again in Browse */
	.caption {
		position: absolute;
		inset-inline: 0;
		inset-block-end: 0;
		background: ${(props) => props.theme.navigationBackGround};
		border-block-start: 1px solid ${(props) => props.theme.navigationFrame};
		color: ${colors.textSafe.grey};
		font-size: 0.72rem;
		line-height: 1.25;
		padding: 0.35rem 0.45rem;
		text-align: start;
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.badge {
		position: absolute;
		inset-block-start: 0.35rem;
		inset-inline-end: 0.35rem;
		background: ${colors.brand.white};
		border: 1px solid ${(props) => props.theme.navigationFrame};
		color: #5335c9;
		font-size: 0.65rem;
		font-weight: 500;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		padding: 0.12rem 0.35rem;
		border-radius: 999px;
	}
`;

function SearchImageView() {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<SearchResult[] | null>(null);
	const [searching, setSearching] = useState(false);
	const [addingUrl, setAddingUrl] = useState<string | null>(null);
	// url → the userDefinedName the file was saved under
	const [addedNames, setAddedNames] = useState<Map<string, string>>(new Map());
	const [lastAddedName, setLastAddedName] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	// broken-thumbnail handling (Openverse's thumb proxy 404s at times):
	// first error retries the tile with the full image, second hides it
	const [thumbFallbacks, setThumbFallbacks] = useState<Set<string>>(new Set());
	const [brokenThumbs, setBrokenThumbs] = useState<Set<string>>(new Set());

	const hostId = useRecoilValue(HostId);
	const ownerName = useRecoilValue(OwnerName);
	const stackId = useRecoilValue(StackId);
	const setFileTypeToDisplay = useSetRecoilState(FileTypeToDisplay);

	const runSearch = async (e: FormEvent) => {
		e.preventDefault();
		const q = query.trim();
		if (!q || searching) return;
		setSearching(true);
		setError(null);
		setResults(null);
		setLastAddedName(null);
		setThumbFallbacks(new Set());
		setBrokenThumbs(new Set());
		try {
			const user = getAuth().currentUser;
			if (!user) throw new Error("No user");
			const jwt = await getIdToken(user);
			const sdk = getSdk(new GraphQLClient(clientConfig.gqlGenerateAiImage()));
			const res = await sdk.SearchPublicDomainImages({ query: q, jwt });
			setResults(res.searchPublicDomainImages?.results ?? []);
		} catch (err) {
			console.error("Image search failed:", err);
			setError(text.fileUploader.searchImagesError);
			setResults([]);
		} finally {
			setSearching(false);
		}
	};

	const addImage = async (result: SearchResult) => {
		if (addingUrl || addedNames.has(result.fullImageUrl)) return;
		setAddingUrl(result.fullImageUrl);
		setError(null);
		try {
			const user = getAuth().currentUser;
			if (!user) throw new Error("No user");
			const jwt = await getIdToken(user);
			const sdk = getSdk(new GraphQLClient(clientConfig.gqlGenerateAiImage()));
			const res = await sdk.ImportPublicDomainImage({
				fullImageUrl: result.fullImageUrl,
				title: result.title,
				source: result.source,
				jwt,
				ownerName,
				stackId,
			});
			if (res.importPublicDomainImage?.__typename === "QpFile") {
				const savedName =
					res.importPublicDomainImage.userDefinedName || result.title;
				setAddedNames((prev) =>
					new Map(prev).set(result.fullImageUrl, savedName),
				);
				setLastAddedName(savedName);
				// tell author to refresh the file list, same signal the AI tab uses
				const bc = new BroadcastChannel(strings.broadcastChannel.author);
				const message: Message = {
					hostId,
					type: "fileUploader",
					action: strings.messages.updateFiles,
				};
				bc.postMessage(message);
				bc.close();
			} else {
				setError(text.fileUploader.searchImagesError);
			}
		} catch (err) {
			console.error("Import failed:", err);
			setError(text.fileUploader.searchImagesError);
		} finally {
			setAddingUrl(null);
		}
	};

	return (
		<StyledSearch>
			<StyledTopRow>
				<StyledBack onClick={() => setFileTypeToDisplay("image")}>
					← {text.fileUploader.searchImagesBack}
				</StyledBack>
				<ToggleFilesPanelButton
					id="search-panel-done"
					testId="search-panel-done"
				/>
			</StyledTopRow>
			<StyledForm onSubmit={runSearch} role="search">
				<StyledSearchInput
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					aria-label={text.fileUploader.searchImagesLabel}
					placeholder={text.fileUploader.searchImagesPlaceholder}
				/>
				<StyledPillButton type="submit" disabled={searching || !query.trim()}>
					{searching
						? text.fileUploader.searchImagesSearching
						: text.fileUploader.searchImagesButton}
				</StyledPillButton>
			</StyledForm>

			{results === null && !searching && (
				<StyledIntro>{text.fileUploader.searchImagesIntro}</StyledIntro>
			)}
			{searching && (
				<StyledStatus aria-live="polite">
					<Spinner aria-hidden="true" focusable="false" />
					{text.fileUploader.searchImagesSearching}
				</StyledStatus>
			)}
			{error && (
				<StyledStatus role="alert" style={{ color: colors.textSafe.red }}>
					{error}
				</StyledStatus>
			)}
			{lastAddedName && !error && (
				<StyledStatus aria-live="polite" style={{ color: "#5335c9" }}>
					{text.fileUploader.searchImagesAddedAs(lastAddedName)}
				</StyledStatus>
			)}
			{results !== null && !searching && results.length === 0 && !error && (
				<StyledStatus>{text.fileUploader.searchImagesNoResults}</StyledStatus>
			)}

			{results && results.length > 0 && (
				<StyledResults>
					{results.map((r) => {
						if (brokenThumbs.has(r.fullImageUrl)) return null;
						const added = addedNames.has(r.fullImageUrl);
						const busy = addingUrl === r.fullImageUrl;
						const useFallback =
							thumbFallbacks.has(r.fullImageUrl) &&
							r.fullImageUrl !== r.thumbnailUrl;
						return (
							<StyledResult key={r.fullImageUrl}>
								<StyledResultButton
									$busy={busy}
									onClick={() => addImage(r)}
									disabled={busy || added}
									aria-label={
										added
											? text.fileUploader.searchImagesAdded
											: text.fileUploader.searchImagesAdd(r.title)
									}
									title={`${r.title} — ${r.source} (${r.license})`}
								>
									<img
										src={useFallback ? r.fullImageUrl : r.thumbnailUrl}
										alt=""
										loading="lazy"
										onError={() => {
											if (!useFallback && r.fullImageUrl !== r.thumbnailUrl) {
												setThumbFallbacks((prev) =>
													new Set(prev).add(r.fullImageUrl),
												);
											} else {
												setBrokenThumbs((prev) =>
													new Set(prev).add(r.fullImageUrl),
												);
											}
										}}
									/>
									{busy && (
										<TileSpinner>
											<Countdown aria-hidden="true" focusable="false" />
										</TileSpinner>
									)}
									{added && (
										<span className="badge">
											{text.fileUploader.searchImagesAdded}
										</span>
									)}
									<span className="caption">
										{r.title} · {r.source}
									</span>
								</StyledResultButton>
							</StyledResult>
						);
					})}
				</StyledResults>
			)}
		</StyledSearch>
	);
}

export default SearchImageView;
